import {
  ActionIcon,
  Badge,
  DefaultMantineColor,
  Popover,
  RingProgress,
  Tooltip,
  useMantineColorScheme,
  useSafeMantineTheme
} from '@mantine/core'
import {P, match} from 'ts-pattern'
import {isBoolean, isPromise, pick, pipe, sortBy, values} from 'remeda'
import {ReactNode, useState} from 'react'
import {okOrShowError, showError} from 'components/notify'
import {ReactionParticipation, ReactionRow, zReactionRow} from './reactions'
import {FaHandSpock} from 'react-icons/fa'
import {ProfileUI} from 'components/portal/profileTypes'
import {usePortal} from 'components/portal/portal'
import {SAvatar, SAvatarGroup} from 'components/portal/SAvatar'
import {sb} from 'utils/db'
import {keyBy, keys} from 'shared/collection'
import {safeParseList} from 'shared/type'
import {StyleProps} from 'shared/ui'
import {useHover} from 'components/hooks/useHover'
import {useQuerySb} from 'utils/db/useQuerySb'
import {IconContext} from 'react-icons'
import {StringNumberInput} from 'components/form/StringNumberInput'
import {ForecastProgress, useForecastProgress} from './progress'
import {ForecasterParticipation} from 'components/portal/admin/AdminParticipation'
import {ComboMulti, ItemMd} from 'components/form/ComboMulti'
import {useStickyOrder} from 'components/hooks/useStickyOrder'
import actionIconMinimalStyles from '../theme/ActionIcon.Minimal.module.css'
import {Notify} from 'api/notify'
import {UseParticipation} from './participation'
import {QuestionCfg} from 'shared/forecast'
import {useQuestionContext} from './questionComponentTypes'
import {useQuestionForm} from './useQuestions'

export type ParticipationStatusProps = {
  participation: Pick<UseParticipation, 'participation' | 'save' | 'cfg'> | {status?: boolean} | undefined
}

const ringSizeProps = (size: number) => ({size, __vars: {'--rp-size': `${size}px`}}) // without this, it would be chopped when body font != 16px

export const ParticipationStatus = ({participation}: ParticipationStatusProps) => {
  const [opened, setOpened] = useState(false)
  const {colors} = useSafeMantineTheme()
  const {profile} = usePortal()
  if (!profile) return <></>
  return match(participation)
    .with({status: P.any}, ({status}) => <ParticipationStatusIcon status={statusFromBool(status)} />)
    .with(
      {save: P.not(P.nullish), participation: P.not(P.nullish), cfg: P.not(P.nullish)},
      ({save, participation: {my_reaction_cfg, participants}, cfg: {tasked, taskedSlots}}) => {
        const untaskedParticipants = participants?.filter(p => !tasked.includes(p)) ?? []
        const myStatus = statusFromBool(my_reaction_cfg?.status)
        const myTasked = tasked.includes(profile.id)
        const myStatusMd = myTasked ? ({color: 'green', desc: 'Tasked'} as const) : statusMds[myStatus]
        return (
          <Popover opened={opened} onChange={setOpened} width={125} position='bottom' withArrow shadow='md'>
            <Popover.Target>
              <div className='flex items-center cursor-pointer'>
                <RingProgress
                  {...ringSizeProps(29)}
                  thickness={3}
                  sections={[
                    {value: ((tasked.length ?? 0) / taskedSlots) * 100, color: 'blue'},
                    {value: ((untaskedParticipants.length ?? 0) / taskedSlots) * 100, color: statusMds['want-to'].color}
                  ]}
                  label={
                    <div className='grid place-items-center'>
                      <ParticipationStatusIcon
                        status={statusFromBool(my_reaction_cfg?.status)}
                        tipRender={
                          <div className='space-y'>
                            <div className='flex gap-1'>
                              <b className='text-green-500'>{tasked.length}</b>
                              <span className='opacity-50'>/</span>
                              <b className='opacity-50'>{taskedSlots}</b> tasked
                            </div>
                            <div>
                              <b style={{color: colors[statusMds['want-to'].color][5]}}>
                                {untaskedParticipants.length}
                              </b>{' '}
                              additional
                              {untaskedParticipants.length > 1 ? 's' : ''} expressed interest
                            </div>
                            <div>
                              <span className='opacity-50'>You: </span>
                              <b style={{color: colors[myStatusMd.color][5]}}> {myStatusMd.desc}</b>
                            </div>
                          </div>
                        }
                        onClick={() => setOpened(true)}
                        tasked={myTasked}
                        className=''
                      />
                    </div>
                  }
                />
              </div>
            </Popover.Target>
            <Popover.Dropdown className='!p-2'>
              <div className='flex w-full justify-center gap-4'>
                {keys(statusMds).map(status => (
                  <ParticipationStatusIcon
                    key={status}
                    status={status}
                    className='cursor-pointer'
                    onClick={async () => {
                      await save({status: statusToBool(status)}).catch(showError)
                      setOpened(false)
                    }}
                  />
                ))}
              </div>
            </Popover.Dropdown>
          </Popover>
        )
      }
    )
    .otherwise(() => <></>)
}

const statusFromBool = (status: boolean | undefined) =>
  match(status)
    .with(true, () => 'want-to' as const)
    .with(false, () => 'not-want' as const)
    .otherwise(() => 'no-response' as const)

const statusToBool = (status: ParticipationStatus | undefined) =>
  match(status)
    .with('want-to', () => true)
    .with('not-want', () => false)
    .otherwise(() => undefined)

const statusMds = {
  'want-to': {
    color: 'indigo',
    desc: 'I want to participate'
  },
  'not-want': {
    color: 'red',
    desc: 'Not this time'
  },
  'no-response': {
    color: 'gray',
    shade: 3,
    desc: 'No response'
  }
} satisfies Record<string, StatusMd>

type StatusMd = {
  color: DefaultMantineColor
  shade?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9
  desc: string
}

type ParticipationStatus = keyof typeof statusMds

export const ParticipationStatusIcon = ({
  status: statusRaw,
  onClick,
  tipRender,
  tasked,
  className
}: {
  status?: ParticipationStatus | boolean | undefined
  onClick?: (() => Promise<void>) | (() => void)
  tipRender?: ReactNode
  tasked?: boolean
} & StyleProps) => {
  const [loading, setLoading] = useState(false)
  const {colors} = useSafeMantineTheme()
  const {ref, hovered} = useHover<HTMLDivElement>()
  const status = statusRaw == null || isBoolean(statusRaw) ? statusFromBool(statusRaw) : statusRaw
  const {desc, color, shade} = {shade: 5, ...statusMds[status ?? 'no-response']}
  const Icon = () => (
    <FaHandSpock
      size={hovered ? '16px' : '15px'}
      style={{padding: hovered ? '0px' : '0.5px', marginLeft: '0'}}
      color={tasked ? colors.green[5] : colors[color][shade]}
    />
  )
  return (
    <div className={className}>
      <IconContext.Provider value={{size: '13px'}}>
        <Tooltip events={{hover: true, focus: false, touch: false}} label={tipRender ?? <div>{desc}</div>}>
          <div>
            {onClick ?
              <ActionIcon
                ref={ref}
                unstyled
                loading={loading}
                size={'26px'}
                classNames={pick(actionIconMinimalStyles, ['loader', 'root'])}
                component='div'
                variant='subtle'
                onClick={() => {
                  if (onClick) {
                    const res = onClick()
                    if (isPromise(res)) {
                      setLoading(true)
                      res.finally(() => setLoading(false))
                    } else onClick()
                  }
                }}>
                <Icon />
              </ActionIcon>
            : <Icon />}
          </div>
        </Tooltip>
      </IconContext.Provider>
    </div>
  )
}

export const QuestionParticipationSection = () => {
  const {question_id, readonly} = useQuestionContext()!
  const {profilesUI} = usePortal()
  const {form} = useQuestionForm({question_id, pick: cfg => pick(cfg, ['tasked', 'taskedSlots'])})

  const {data: participations} = useQuerySb('reaction', ['profileParticipation', {question_id}], async () => {
    const {data, error} = await sb
      .from('reaction')
      .select('*')
      .match({question_id, type: 'participation', deleted: false})
    if (error) {
      showError(error)
      return undefined
    }
    return safeParseList(zReactionRow, data) as ReactionRow<'participation'>[]
  })
  const tasked = form.values?.tasked

  const {getForecasterProgress} = useForecastProgress({filter: {question_id}})

  const {data: forecasters, reset: resetForecastersOrder} = useStickyOrder(
    profilesUI && tasked ?
      pipe(
        values(profilesUI)
          .map(profile => ({
            profile,
            participation: participations?.find(p => p.created_by == profile.id)?.cfg,
            progress: getForecasterProgress({question_id, profile_id: profile.id}),
            tasked: tasked.includes(profile.id),
            label: profile.name,
            id: profile.id
          }))
          .filter(f => f.profile.roles.includes('forecaster') || f.tasked || f.participation?.status),
        sortBy(
          f => (f.tasked ? 0 : 1),
          f =>
            match(f.participation?.status)
              .with(true, () => 0)
              .with(false, () => 2)
              .otherwise(() => 1),
          [f => f.progress?.completed ?? 0, 'desc'],
          f => f.profile.name.toLowerCase()
        )
      )
    : undefined
  )

  if (!profilesUI || !participations || !tasked || !forecasters) return <></>

  const slotsInput = form.getInputProps('taskedSlots')

  return (
    <div className='space-y-12 mb-[8em]'>
      {!readonly && (
        <div className='flex gap-8 items-center'>
          <div className='flex items-center gap-1'>
            <StringNumberInput className='w-20' {...{...slotsInput, onChange: v => slotsInput.onChange(v)}} />
            <span>slots</span>
          </div>
          <div className='flex gap-4'>
            <div>
              <b className='text-lg'>{tasked.length}</b> tasked (
              <b>{forecasters.filter(f => f.participation?.status == true).length}</b> expressed interest)
            </div>
          </div>
          {!readonly && (
            <ComboMulti<string, ProfileTasked>
              md={keyBy(forecasters, f => f.profile.id)}
              item={p => <ParticipationForecasterItem {...p} />}
              value={tasked}
              showSearch
              searchPlaceholder='Search forecasters'
              disableSort
              buttonProps={{size: 'md'}}
              onDropDownClose={resetForecastersOrder}
              onChange={async (newTasked, delta) => {
                form.setFieldValue('tasked', newTasked)
                if (delta.mode == 'clear') {
                  console.error('clear not supported on the tasked editor')
                  return
                }
                const {value: profile_id, mode} = delta
                await Notify.onQuestionChange({part: 'tasked', question_id, profile_id, mode}).then(okOrShowError)
              }}
              target={items => (
                <>
                  <span>tasked forecasters</span>
                  <SAvatarGroup className='ml-2' profiles={items.filter(p => p.selected).map(p => p.profile)} />
                </>
              )}
            />
          )}
        </div>
      )}
      <div>
        <ForecasterParticipation question_id={question_id} />
      </div>
    </div>
  )
}

type ProfileTasked = {
  profile: ProfileUI
  participation?: ReactionParticipation
  progress?: ForecastProgress
  tasked: boolean
} & ItemMd

const ParticipationForecasterItem = ({profile, participation, progress, tasked}: ProfileTasked) => (
  <div className='flex items-center gap-2'>
    <ParticipationStatusIcon status={statusFromBool(participation?.status)} tasked={tasked} />
    <SAvatar size='md' profile={profile} className='ml-2' />
    <p className='sensitive'>{profile.name}</p>
    {progress && <TaskedProgressText progress={progress} />}
  </div>
)

export const TaskedProgressText = ({
  progress: {completed, total},
  variant = 'sm',
  className
}: {
  progress: ForecastProgress
  variant?: 'sm' | 'lg'
} & StyleProps) => {
  const theme = useSafeMantineTheme()
  const {colorScheme} = useMantineColorScheme()
  const {colors} = theme
  const color = completed == total ? 'green' : 'amber'
  const subtleProps = {color: colors.gray[colorScheme == 'dark' ? 3 : 5]}
  return (
    <span className={className}>
      <span className={`font-bold`} style={{color: theme.colors[color]?.[7]}}>
        {completed}
      </span>
      {total != null && (
        <>
          <span style={subtleProps}>/</span>
          <span className='font-bold' style={subtleProps}>
            {total}
          </span>
        </>
      )}
      {variant == 'lg' && (
        <span style={subtleProps}>
          {' '}
          active forecasts completed. <br /> Forecasts are active when the question/condition is in Forecasting/Approved
          status.
        </span>
      )}
    </span>
  )
}

export const TaskedStatus = ({isTasked, progress}: {isTasked?: boolean; progress?: ForecastProgress}) => {
  const taskedDone = progress == null ? undefined : progress.completed >= (progress.total ?? 0)
  const taskedColor =
    taskedDone == null ? 'gray'
    : taskedDone ? 'green'
    : 'amber'
  return isTasked && progress ?
      <Tooltip
        label={
          <div>
            <div className={`${progress ? 'font-bold' : ''}`}>You&apos;re tasked to forecast on this question.</div>
            <div className='mt-2'>
              <TaskedProgressText progress={progress} variant='lg' className='text-' />
            </div>
          </div>
        }>
        <Badge variant='dot' size='lg' color={taskedColor}>
          <span className='pr-1 text-xs text-gray-500'>{taskedDone ? 'done' : 'todo'}</span>
          <TaskedProgressText progress={progress} />
        </Badge>
      </Tooltip>
    : <></>
}
